import React from 'react';

import { Box, Flex } from '@rover/kibble/core';
import {
  shouldLazyLoadImage,
  shouldSimplifiedCardsShowLegacyPrice,
  shouldSimplifiedCardsShowReviewSnippet,
} from '@rover/react-lib/src/pages/search/SearchPage/utilities/searchCardTreatment';
import { isReactNative } from '@rover/rsdk/src/modules/Env/react';
import type { CommonSearchResult } from '@rover/types/src/CommonSearchResult';
import type { PriceTransparencyLevel } from '@rover/types/src/PriceTransparencyLevel';
import { SearchCardTreatment } from '@rover/types/src/SearchCardTreatment';

import SimplifiedNameSection from './InfoColumn/SimplifiedNameSection';
import LegacyPriceColumn from './PriceColumn/LegacyPriceColumn';
import ImageColumn from './ImageColumn';
import SearchScoreData from './SearchScoreData';
import SitterHighlightsSection from './SitterHighlightsSection';
import SkillTagRow from './SkillTagRow';

// Price box is absolutely positioned, so flex can't size it. LEGACY_PRICE_COLUMN_MIN_WIDTH
// sets its width; INFO_ROW_PADDING_RIGHT pads the info-row by at least that much so info
// text wraps before reaching the price box (otherwise they overlap).
const LEGACY_PRICE_COLUMN_MIN_WIDTH = '120px';
const INFO_ROW_PADDING_RIGHT = '32x';

export type CommonProps = {
  index: number | undefined;
  language: string;
  showFromText: boolean;
  isBHUser: boolean;
  shouldShowPriceInCredits: boolean;
  priceTransparencyLevel?: PriceTransparencyLevel;
  cardTreatment: SearchCardTreatment;
  preferCatReviews: boolean;
};

type Props = CommonProps & {
  searchResult: CommonSearchResult;
  sectionRef?: React.RefObject<HTMLElement>;
};

const SimplifiedCardContent = ({
  searchResult,
  index,
  language,
  showFromText,
  isBHUser,
  shouldShowPriceInCredits,
  priceTransparencyLevel,
  cardTreatment,
  sectionRef,
  preferCatReviews,
}: Props): React.ReactElement => {
  const { avatarUrl, avatarSmallUrl, popoverDebugData, shortName } = searchResult;

  const displayExpanded = false;

  const sitterAvatar = (
    <ImageColumn
      displayExpanded={displayExpanded}
      desktopProfileImage={avatarUrl}
      mobileProfileImage={avatarSmallUrl}
      shortName={shortName}
      lazy={shouldLazyLoadImage(index)}
      badge={undefined}
      cardTreatment={cardTreatment}
    />
  );

  const showLegacyPrice = shouldSimplifiedCardsShowLegacyPrice(cardTreatment);

  const basicInfo = (
    <SimplifiedNameSection
      searchResult={searchResult}
      showPrice={!showLegacyPrice}
      language={language}
      showFromText={showFromText}
      isBHUser={isBHUser}
      shouldShowPriceInCredits={shouldShowPriceInCredits}
      priceTransparencyLevel={priceTransparencyLevel}
    />
  );

  const sitterHighlights = (
    <SitterHighlightsSection
      searchResult={searchResult}
      preferCatReviews={preferCatReviews}
      showReviewSnippet={shouldSimplifiedCardsShowReviewSnippet(cardTreatment)}
    />
  );

  const skillTags = isReactNative() ? (
    <SkillTagRow tags={searchResult.personalizationTags} />
  ) : null;

  const legacyPriceElement = showLegacyPrice ? (
    <Box position="absolute" right="0" minWidth={LEGACY_PRICE_COLUMN_MIN_WIDTH}>
      <LegacyPriceColumn
        currencyCode={searchResult.currencyCode}
        language={language}
        price={searchResult.price}
        priceInCredits={searchResult.priceInCredits}
        shouldShowPriceInCredits={shouldShowPriceInCredits}
        showFromText={showFromText}
        unitAsSentence={searchResult.unitAsSentence}
        priceTransparencyLevel={priceTransparencyLevel}
        isBHUser={isBHUser}
      />
    </Box>
  ) : null;

  const mobileLayout = (
    <Flex flexDirection="column" gap="3x" data-testid="simplified-card-content-mobile">
      <Flex flexDirection="row" gap="3x" pr={showLegacyPrice ? INFO_ROW_PADDING_RIGHT : '0x'}>
        {sitterAvatar}
        {/* Wrapper required by RxN to not overflow past the edge of the screen */}
        <Flex flexShrink="1" width="100%">
          {basicInfo}
        </Flex>
      </Flex>
      {legacyPriceElement}
      {sitterHighlights}
      {skillTags}
    </Flex>
  );

  // Disable rendering RxN to avoid being read by screen readers and improve performance.
  const webLayout = isReactNative() ? null : (
    <Flex flexDirection="row" gap="4x" data-testid="simplified-card-content-web">
      {sitterAvatar}
      <Flex flexDirection="column" gap="3x" width="100%">
        {basicInfo}
        {popoverDebugData && <SearchScoreData data={popoverDebugData} />}
        {sitterHighlights}
      </Flex>
    </Flex>
  );

  return (
    // Box.tsx and Box.native.tsx ref prop are incompatible
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    <Box ref={sectionRef as any}>
      <Box display={['block', 'none']}>{mobileLayout}</Box>
      <Box display={['none', 'block']}>{webLayout}</Box>
    </Box>
  );
};

export default SimplifiedCardContent;
